import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute } from "@angular/router";
import { LoadingDialogComponent } from "@cotecna/dialogs";
import {
  EventHistory,
  EventHistoryApi,
  EventHistoryDocument,
  EventMetadata,
  InspectionAuditActions,
  InspectionAuditActionsApi,
} from "src/app/inspection/model/event-history";
import { AuditService } from "src/app/inspection/services/audit-service";

const PageSize: number = 20;

@Component({
  selector: "im-events-history",
  imports: [
    MatIconModule,
    MatPaginatorModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: "./events-history.component.html",
  styleUrl: "./events-history.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EventsHistoryComponent implements OnInit {
  private inspectionId: string;

  private pagSize: number = PageSize;
  public currentPage: number = 0;
  public lastPage: boolean = false;
  public dataLoaded: boolean = false;

  public events: EventHistory[] = [];

  constructor(
    private auditService: AuditService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.getInspectionIdFromUrl();
      const events = await this.auditService.getEventsHistoryData(
        this.inspectionId,
        this.currentPage,
        this.pagSize,
      );
      if (events.length < this.pagSize) this.lastPage = true;
      this.formatData(events);
      this.dataLoaded = true;
    } catch {
      this.lastPage = true;
      this.dataLoaded = true;
    }
    this.changeDetectorRef.markForCheck();
  }

  public async nextPageButton() {
    try {
      this.dataLoaded = false;
      this.currentPage++;
      const events = await this.auditService.getEventsHistoryData(
        this.inspectionId,
        this.currentPage,
        this.pagSize,
      );
      if (events.length < this.pagSize) this.lastPage = true;
      this.formatData(events);
      this.dataLoaded = true;
    } catch {
      this.lastPage = true;
      this.dataLoaded = true;
    }
    this.changeDetectorRef.markForCheck();
  }

  public async previousPageButton() {
    try {
      this.dataLoaded = false;
      this.currentPage--;
      this.lastPage = false;
      const events = await this.auditService.getEventsHistoryData(
        this.inspectionId,
        this.currentPage,
        this.pagSize,
      );
      this.formatData(events);
      this.dataLoaded = true;
    } catch {
      this.lastPage = true;
      this.dataLoaded = true;
    }
    this.changeDetectorRef.markForCheck();
  }

  public async downloadDocument(document: EventHistoryDocument): Promise<void> {
    const loadingDialog = this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: "Downloading Document",
    });
    await this.auditService.downloadAttachedDocument(
      this.inspectionId,
      document,
    );
    loadingDialog?.close();
  }

  private formatData(events: EventHistoryApi[]): void {
    this.events = [];
    events.forEach((event) => {
      this.events.push({
        eventDate: this.formatDate(event.eventDate),
        userName: event.userName,
        action: this.formatAction(event.action),
        description: this.formatDescription(event.action, event.metadata),
        document: this.hasDocument(event.action, event.metadata),
      });
    });
  }

  private formatDate(eventDate: string): string {
    const date = new Date(eventDate);
    const localDate = date.toLocaleString();
    return localDate;
  }

  private formatAction(action: number): InspectionAuditActions {
    switch (action) {
      case InspectionAuditActionsApi.InspectionCreated:
        return InspectionAuditActions.InspectionCreated;
      case InspectionAuditActionsApi.InspectionStateChanged:
        return InspectionAuditActions.InspectionStateChanged;
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldRemoved:
        return InspectionAuditActions.GeneralInfoAdditionalFields_FieldRemoved;
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldCreated:
        return InspectionAuditActions.GeneralInfoAdditionalFields_FieldCreated;
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldChangeName:
        return InspectionAuditActions.GeneralInfoAdditionalFields_FieldChangeName;
      case InspectionAuditActionsApi.InspectorAssigned:
        return InspectionAuditActions.InspectorAssigned;
      case InspectionAuditActionsApi.InspectorUnassigned:
        return InspectionAuditActions.InspectorUnassigned;
      case InspectionAuditActionsApi.ReviewComment:
        return InspectionAuditActions.ReviewComment;
      case InspectionAuditActionsApi.ReAssignInspector:
        return InspectionAuditActions.ReAssignInspector;
      default:
        break;
    }
  }

  private formatDescription(
    action: number,
    metadata: EventMetadata[],
  ): string[] {
    switch (action) {
      case InspectionAuditActionsApi.InspectionCreated:
        return [`Status: ${metadata[0].value}`];
      case InspectionAuditActionsApi.InspectionStateChanged:
        return [`Status: ${metadata[0].value}`];
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldRemoved:
        return [metadata[0].value];
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldCreated:
        return [metadata[0].value];
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldChangeName:
        return [`${metadata[1].value} to ${metadata[0].value}`];
      case InspectionAuditActionsApi.InspectorAssigned:
        return [metadata[0].value];
      case InspectionAuditActionsApi.InspectorUnassigned:
        return [metadata[0].value];
      case InspectionAuditActionsApi.ReviewComment:
        return [metadata[0].value];
      case InspectionAuditActionsApi.ReAssignInspector:
        return [metadata[0].value, metadata[1].value];
      default:
        break;
    }
  }

  private hasDocument(
    action: number,
    metadata: EventMetadata[],
  ): EventHistoryDocument {
    if (action == InspectionAuditActionsApi.ReAssignInspector) {
      if (metadata.length == 4) {
        return {
          id: metadata[2].value,
          name: metadata[3].value,
        } as EventHistoryDocument;
      }
    }
    return null;
  }

  private getInspectionIdFromUrl(): void {
    this.activatedRoute.parent?.params.subscribe((params) => {
      this.inspectionId = params["id"];
    });
  }
}
